import { useLazyQuery } from '@apollo/client';
import { ResultOf } from '@graphql-typed-document-node/core';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Avatar,
  AvatarFallback,
  Combobox,
  ComboboxDropdown,
  ComboboxSelectButton,
  Icon,
  Stack,
  tv,
} from '@eluve/components';
import {
  AppointmentPatientIdFragment,
  useAppointmentId,
} from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';

import { PatientComboboxContent } from './PatientComboboxContent';
import { PatientOption } from './PatientOption';
import { patientOptionFragment, searchPatientsQuery } from './operations';

interface PatientSelectorProps {
  disabled?: boolean;
  tenantId: string;
  onPatientSelected?: (patientId?: string) => void;
}

const selectPatientVariant = tv({
  base: 'w-full px-4 hover:bg-bg-quaternary',
  variants: {
    disabled: {
      true: 'pointer-events-none',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

export const PatientSelector: React.FC<PatientSelectorProps> = ({
  tenantId,
  onPatientSelected,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [searchResults, setSearchResults] = useState<
    ResultOf<typeof patientOptionFragment>[]
  >([]);
  const [searchPatients, { loading }] = useLazyQuery(searchPatientsQuery);

  const appointmentId = useAppointmentId();

  const { patientId } = useCompleteFragment({
    fragment: AppointmentPatientIdFragment,
    key: {
      id: appointmentId,
    },
  });

  const selectedPatient = useCompleteFragment({
    fragment: patientOptionFragment,
    key: {
      id: patientId ?? '',
    },
    strict: false,
  });

  const handlePatientSelected = (patientId?: string) => {
    onPatientSelected?.(patientId);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        setSearchResults([]);

        if (!query || query.length < 2) {
          return;
        }

        searchPatients({
          variables: { query, limit: 25, tenantId },
        }).then((result) => {
          const patients = (result.data?.searchPatients ?? []).map(
            (patient) => ({
              ...patient,
            }),
          );
          setSearchResults(patients);
        });
      }, 250),
    [searchPatients, tenantId],
  );

  useEffect(
    function cancelDebounceOnUnmount() {
      return () => debouncedSearch.cancel();
    },
    [debouncedSearch],
  );

  return (
    <Combobox disabled={disabled}>
      <ComboboxSelectButton className={selectPatientVariant({ disabled })}>
        {selectedPatient ? (
          <PatientOption {...selectedPatient} />
        ) : (
          <Stack direction="horizontal" fullWidth={false} gap={2}>
            <Avatar size="lg">
              <AvatarFallback>
                <Icon name="ContactRound" />
              </AvatarFallback>
            </Avatar>
            <span className="text-text-secondary">
              {t('smartBlocks.patientSelector.selectPatient')}
            </span>
          </Stack>
        )}
      </ComboboxSelectButton>
      <ComboboxDropdown
        className="pointer-events-auto"
        searchPlaceholder={t('smartBlocks.patientSelector.searchPlaceholder')}
        shouldFilter={false}
      >
        <PatientComboboxContent
          selectedPatient={selectedPatient}
          searchResults={searchResults}
          loading={loading}
          handlePatientSelected={handlePatientSelected}
          tenantId={tenantId}
          debouncedSearch={debouncedSearch}
          setSearchResults={setSearchResults}
        />
      </ComboboxDropdown>
    </Combobox>
  );
};
